import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Input, message } from "antd";
import _ from "lodash";
import React from "react";
import { connect } from "react-redux";
import { logError, logUserReport } from "redux/actions";
import { v4 as uuidv4 } from "uuid";
import logo from "../../images/new-branding-2.0/aivr-hybrid.svg";
import OBCButton from "../OBC/OBCButton";
import { ERROR_BOUNDARY } from "./ErrorConstants";

const { TextArea } = Input;

class ErrorBoundaryComponent extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            uuid: "",
            userFeedback: "",
            hasSubmitted: false,
        };
    }

    componentDidCatch(error, info) {
        const uuid = uuidv4();

        this.setState({
            uuid: uuid,
            hasError: true,
            userFeedback: "",
            hasSubmitted: false,
        });

        // Session ID is already safely extracted in mapStateToProps
        const sessionIdForLog = this.props.selectedSessionID || "No session selected";

        const errorMetadata = {
            message: error.message,
            stack: error.stack,
            componentStack: info.componentStack,
            url: window.location.href,
            selectedSessionID: sessionIdForLog,
            uuid,
            errorType: ERROR_BOUNDARY,
            frontendVersion: process.env["REACT_APP_GIT_VERSION"],
        };

        if (window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
            this.props.dispatch(logError(errorMetadata));
        }
    }

    handleFeedbackChange = (e) => {
        this.setState({ userFeedback: e.target.value });
    };

    messageSubmitSuccess = () => {
        message.success("Report submitted successfully");
        this.setState({ hasSubmitted: true });
    };

    messageSubmitFail = () => {
        message.error("Error submitting report");
    };

    handleSubmitFeedback = () => {
        const { uuid, userFeedback } = this.state;

        if (!userFeedback.trim()) {
            message.warning("Please provide some feedback before submitting.");
            return;
        }

        this.props.dispatch(logUserReport(uuid, userFeedback, this.messageSubmitFail, this.messageSubmitSuccess));
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="ErrorBoundaryWrapper">
                    <div className="ErrorBoundary">
                        <div className="LogoContainer">
                            <img
                                className="ErrorBoundaryLogo"
                                src={logo}
                                alt="AIVR Logo"
                                crossOrigin={"anonymous"}
                            />
                        </div>
                        <div className="MessageContainer">
                            <h1>An unexpected error occured</h1>
                            <p className="ErrorBoundaryText">
                                Apologies for the inconvenience. The error has been automatically reported to our engineering team.
                            </p>

                            <OBCButton
                                className="ErrorRefreshButton"
                                onClick={() => window.location.reload()}>
                                Refresh the page
                            </OBCButton>
                        </div>
                        <div className="ContactContainer">
                            {!this.state.hasSubmitted && (
                                <div className="ContactContainer">
                                    <h2>Additional Feedback</h2>
                                    <p>Please describe what you were doing before the error occurred. This will help us diagnose the issue more effectively.</p>
                                    <TextArea
                                        rows={4}
                                        value={this.state.userFeedback}
                                        onChange={this.handleFeedbackChange}
                                        placeholder="Describe your actions before the error occurred..."
                                    />
                                    <OBCButton
                                        className="ErrorRefreshButton"
                                        onClick={this.handleSubmitFeedback}>
                                        Submit Feedback
                                    </OBCButton>
                                </div>
                            )}
                        </div>
                        <div className="ContactContainer">
                            <h2>Contacting us</h2>
                            <div className="UuidContainer">
                                <p>{this.state.uuid}</p>
                                <FontAwesomeIcon
                                    onClick={() => {
                                        navigator.clipboard.writeText(this.state.uuid);
                                    }}
                                    className="CopyIcon"
                                    icon={faCopy}
                                />
                            </div>
                            <p>
                                If you would like to{" "}
                                <a href={`mailto:support@onebigcircle.co.uk?subject=AIVR Platform bug - Ref ${this.state.uuid}`}>contact us</a>, please quote
                                this unique reference number.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const mapStateToProps = (state) => {
    return {
        selectedSessionID: _.get(state, ["playlist", "data", "routeID"], null),
    };
};

const ErrorBoundary = connect(mapStateToProps)(ErrorBoundaryComponent);
export default ErrorBoundary;
